import React, { useContext, useMemo, useState } from 'react';
import { Tag, Typography, Button, Alert } from 'antd';
import {
  EditOutlined,
  FileTextOutlined,
  LinkOutlined,
  ReloadOutlined,
  CopyOutlined,
  ShopOutlined,
  SyncOutlined,
  TagsOutlined,
} from '@ant-design/icons';
import { orDash, copyToClipboard } from '../utils/format';
import useModules from '../../../hooks/useModules';
import { EInvoiceStatus, EModule } from '../../../enums';
import { IInvoiceDetails } from '../interfaces';
import { convertMoedaBRDoJSNovo, formatDateStringToDateAndTime } from '../../../utils/Conversores';
import { formatCpfCnpj } from '../../../utils/Masks';
import StatusTag from '../../CityInvoices/components/StatusTag';
import { TicketTags } from '../../../components/TicketTags';
import { UpdateInvoiceStatusModal } from '../../../components/UpdateInvoiceStatusModal';
import useAuth from '../../../hooks/useAuth';
import { ThemeContext } from '../../../contexts/ThemeContext';

const { Text } = Typography;

interface InvoiceInfoCardProps {
  invoice: IInvoiceDetails;
  onReload: () => void;
  onReprocess: (invoiceId: number, appUserId: number | undefined) => void;
  isReprocessing: boolean;
}

export const InvoiceInfoCard: React.FC<InvoiceInfoCardProps> = ({
  invoice,
  onReload,
  onReprocess,
  isReprocessing,
}) => {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === 'dark';

  const { cityModules } = useModules();
  const [editStatusOpen, setEditStatusOpen] = useState(false);
  const hasPointsModule = cityModules.includes(EModule.REWARD);
  const { user } = useAuth();
  const isAdmin = user?.role?.id === 1;

  const isReprocessBlocked = useMemo(() => {
    if (!invoice.updated_at) return false;

    const attemptDate = new Date(invoice.updated_at);
    const now = new Date();

    const diffMinutes = (now.getTime() - attemptDate.getTime()) / 1000 / 60;

    return diffMinutes < 5;
  }, [invoice.updated_at]);

  const cardBg = isDark ? '#141414' : '#ffffff';
  const itemBg = isDark ? '#1f1f1f' : '#f8fafc';
  const itemBorder = isDark ? '#2a2a2a' : '#eaedf1';
  const labelColor = isDark ? '#8c8c8c' : '#64748b';
  const textColor = isDark ? '#f0f0f0' : '#1e293b';

  return (
    <div
      style={{
        background: cardBg,
        borderRadius: 14,
        border: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
        boxShadow: isDark
          ? '0 4px 20px rgba(0, 0, 0, 0.35)'
          : '0 2px 10px rgba(0, 0, 0, 0.03)',
        overflow: 'hidden',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          padding: '16px 20px',
          borderBottom: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 12,
          background: isDark ? '#1a1a1a' : '#fafafa',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 8,
              background: isDark ? 'rgba(22, 119, 255, 0.15)' : 'rgba(22, 119, 255, 0.1)',
              color: '#1677ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 18,
            }}
          >
            <FileTextOutlined />
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: textColor }}>
              Dados da Nota Fiscal
            </div>
            <div style={{ fontSize: 12, color: labelColor }}>
              Informações cadastrais e detalhes de emissão
            </div>
          </div>
        </div>

        {isAdmin && (
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => setEditStatusOpen(true)}
            style={{
              borderRadius: 6,
              fontSize: 12,
              fontWeight: 500,
            }}
          >
            Alterar status
          </Button>
        )}
      </div>

      <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: 16, flex: 1 }}>
        {invoice.message && (
          <Alert
            type={
              invoice.status === EInvoiceStatus.error ? 'error'
                : invoice.status === EInvoiceStatus.warning ? 'warning'
                  : invoice.status === EInvoiceStatus.completed ? 'success'
                    : 'info'
            }
            showIcon
            message={
              <span style={{ fontSize: 13, lineHeight: 1.4 }}>
                <strong style={{ marginRight: 6 }}>Mensagem:</strong>
                <span>{invoice.message}</span>
              </span>
            }
            style={{
              borderRadius: 8,
              padding: '6px 12px',
            }}
          />
        )}
        {invoice.key && (
          <div
            style={{
              background: itemBg,
              borderRadius: 8,
              border: `1px solid ${itemBorder}`,
              padding: '10px 14px',
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: labelColor, textTransform: 'uppercase' }}>
                Chave de Acesso
              </span>
              <Button
                type="text"
                size="small"
                icon={<CopyOutlined />}
                onClick={() => copyToClipboard(invoice.key, 'Chave de acesso')}
                style={{ fontSize: 11, height: 22, padding: '0 6px', color: '#1677ff' }}
              >
                Copiar
              </Button>
            </div>
            <Text
              code
              style={{
                fontSize: 12,
                wordBreak: 'break-all',
                background: 'transparent',
                border: 'none',
                padding: 0,
                color: isDark ? '#60a5fa' : '#0958d9',
                fontWeight: 600,
              }}
            >
              {invoice.key}
            </Text>
          </div>
        )}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 10,
          }}
        >
          <div style={{ background: itemBg, borderRadius: 8, border: `1px solid ${itemBorder}`, padding: '10px 12px' }}>
            <div style={{ fontSize: 11, color: labelColor, marginBottom: 2 }}>ID da Nota</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Text strong style={{ fontSize: 14, color: textColor }}>#{invoice.id}</Text>
            </div>
          </div>

          <div style={{ background: itemBg, borderRadius: 8, border: `1px solid ${itemBorder}`, padding: '10px 12px' }}>
            <div style={{ fontSize: 11, color: labelColor, marginBottom: 2 }}>Documento Fiscal</div>
            <div>
              {invoice.link ? (
                <a
                  href={invoice.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: 'none' }}
                >
                  <Tag
                    color="blue"
                    icon={<LinkOutlined />}
                    style={{
                      cursor: 'pointer',
                      fontWeight: 600,
                      margin: 0,
                      fontSize: 12,
                      padding: '1px 8px',
                      borderRadius: 4,
                    }}
                  >
                    Abrir NFC-e
                  </Tag>
                </a>
              ) : (
                <span style={{ fontSize: 13, color: labelColor }}>--</span>
              )}
            </div>
          </div>

          <div style={{ background: itemBg, borderRadius: 8, border: `1px solid ${itemBorder}`, padding: '10px 12px' }}>
            <div style={{ fontSize: 11, color: labelColor, marginBottom: 2 }}>Valor da Nota</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#52c41a' }}>
              {invoice.value ? `R$ ${convertMoedaBRDoJSNovo(invoice.value)}` : '--'}
            </div>
          </div>

          <div style={{ background: itemBg, borderRadius: 8, border: `1px solid ${itemBorder}`, padding: '10px 12px' }}>
            <div style={{ fontSize: 11, color: labelColor, marginBottom: 2 }}>Data de Emissão</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: textColor }}>
              {invoice.date_issue ? formatDateStringToDateAndTime(invoice.date_issue) : '--'}
            </div>
          </div>
          <div style={{ background: itemBg, borderRadius: 8, border: `1px solid ${itemBorder}`, padding: '10px 12px' }}>
            <div style={{ fontSize: 11, color: labelColor, marginBottom: 2 }}>Tipo de Documento</div>
            <div>
              <Tag color="blue" style={{ fontWeight: 600, margin: 0 }}>
                {invoice.type || '--'}
              </Tag>
            </div>
          </div>

          <div style={{ background: itemBg, borderRadius: 8, border: `1px solid ${itemBorder}`, padding: '10px 12px' }}>
            <div style={{ fontSize: 11, color: labelColor, marginBottom: 2 }}>Status</div>
            <div>
              <StatusTag status={invoice.status} message={invoice.message} />
            </div>
          </div>
        </div>
        <div
          style={{
            background: itemBg,
            borderRadius: 8,
            border: `1px solid ${itemBorder}`,
            padding: '12px 14px',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: textColor }}>
            <ShopOutlined style={{ color: '#1677ff' }} />
            <span>Estabelecimento Emissor</span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
            <div>
              <div style={{ fontSize: 11, color: labelColor }}>Razão Social / Empresa</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: textColor }}>
                {orDash(invoice.company)}
              </div>
            </div>

            <div>
              <div style={{ fontSize: 11, color: labelColor }}>CNPJ / CPF</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: textColor }}>
                {invoice.cpf_cnpj ? formatCpfCnpj(invoice.cpf_cnpj) : '--'}
              </div>
            </div>

            <div>
              <div style={{ fontSize: 11, color: labelColor }}>Município de Emissão</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: textColor }}>
                {orDash(invoice.city_issue)}
              </div>
            </div>

            <div>
              <div style={{ fontSize: 11, color: labelColor }}>UF de Emissão</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: textColor }}>
                {orDash(invoice.state_issue)}
              </div>
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <div style={{ fontSize: 11, color: labelColor }}>Endereço</div>
              <div style={{ fontSize: 12, color: textColor }}>
                {orDash(invoice.address)}
              </div>
            </div>
          </div>
        </div>
        {hasPointsModule && (
          <div
            style={{
              background: itemBg,
              borderRadius: 8,
              border: `1px solid ${itemBorder}`,
              padding: '12px 14px',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 700, color: textColor }}>
              Pontuação de Recompensa
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
              <div>
                <div style={{ fontSize: 11, color: labelColor }}>Pontos Gerados</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#52c41a' }}>
                  {orDash(invoice.reward_points)}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: labelColor }}>Pontos Utilizados</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#fa8c16' }}>
                  {orDash(invoice.points_used)}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: labelColor }}>Pontos Expirados</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#ff4d4f' }}>
                  {orDash(invoice.points_expired)}
                </div>
              </div>
            </div>
          </div>
        )}
        <div
          style={{
            background: itemBg,
            borderRadius: 8,
            border: `1px solid ${itemBorder}`,
            padding: '14px',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, color: textColor }}>
              <SyncOutlined style={{ color: '#fa8c16' }} />
              <span>Auditoria & Processamento</span>
            </div>

            {invoice.status !== EInvoiceStatus.completed && !isReprocessBlocked && (
              <Button
                type="primary"
                size="small"
                icon={<ReloadOutlined />}
                onClick={() => onReprocess(invoice.id, invoice.app_user?.id)}
                loading={isReprocessing}
                style={{
                  backgroundColor: '#fa8c16',
                  borderColor: '#fa8c16',
                  borderRadius: 6,
                  fontWeight: 600,
                  fontSize: 12,
                }}
              >
                Reprocessar nota
              </Button>
            )}
          </div>

          {isReprocessBlocked && (
            <Alert
              type="warning"
              showIcon
              message="Aguarde 5 minutos para reprocessar esta nota fiscal."
              style={{
                padding: '6px 12px',
                fontSize: 12,
                borderRadius: 6,
              }}
            />
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
            <div>
              <div style={{ fontSize: 11, color: labelColor }}>Tentativas</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: textColor }}>
                {orDash(invoice.processing_attempts)} / 3
              </div>
            </div>

            <div>
              <div style={{ fontSize: 11, color: labelColor }}>Última Tentativa</div>
              <div style={{ fontSize: 12, fontWeight: 500, color: textColor }}>
                {invoice.attempt_date ? formatDateStringToDateAndTime(invoice.attempt_date) : '--'}
              </div>
            </div>

            <div>
              <div style={{ fontSize: 11, color: labelColor }}>Bilhetes</div>
              <div>
                <Tag color={invoice.ticket_processed ? 'success' : 'default'} style={{ margin: 0, fontWeight: 500 }}>
                  {invoice.ticket_processed ? 'Processado' : 'Pendente'}
                </Tag>
              </div>
            </div>

            {hasPointsModule && (
              <div>
                <div style={{ fontSize: 11, color: labelColor }}>Pontos</div>
                <div>
                  <Tag color={invoice.points_processed ? 'success' : 'default'} style={{ margin: 0, fontWeight: 500 }}>
                    {invoice.points_processed ? 'Processado' : 'Pendente'}
                  </Tag>
                </div>
              </div>
            )}

            {invoice.process_after && (
              <div>
                <div style={{ fontSize: 11, color: labelColor }}>Processar após</div>
                <div>
                  <Tag color="gold" style={{ margin: 0, fontWeight: 500 }}>
                    {formatDateStringToDateAndTime(invoice.process_after)}
                  </Tag>
                </div>
              </div>
            )}

            <div>
              <div style={{ fontSize: 11, color: labelColor }}>Criada em</div>
              <div style={{ fontSize: 12, color: textColor }}>
                {formatDateStringToDateAndTime(invoice.created_at)}
              </div>
            </div>

            <div>
              <div style={{ fontSize: 11, color: labelColor }}>Atualizada em</div>
              <div style={{ fontSize: 12, color: textColor }}>
                {formatDateStringToDateAndTime(invoice.updated_at)}
              </div>
            </div>
          </div>
        </div>

        <div
          style={{
            background: itemBg,
            borderRadius: 8,
            border: `1px solid ${itemBorder}`,
            padding: '14px',
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, color: textColor }}>
              <TagsOutlined style={{ color: '#13c2c2' }} />
              <span>Bilhetes Gerados nesta Nota</span>
            </div>
            <Tag color={invoice.tickets?.length ? 'cyan' : 'default'}>
              {invoice.tickets?.length || 0} bilhete(s)
            </Tag>
          </div>

          {invoice.tickets?.length ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
              <TicketTags tickets={invoice.tickets.map((t) => t.id)} />
            </div>
          ) : (
            <div style={{ padding: '10px 0', textAlign: 'center' }}>
              <Text type="secondary" italic style={{ fontSize: 12 }}>
                Nenhum bilhete foi gerado para esta nota fiscal.
              </Text>
            </div>
          )}
        </div>
      </div>

      <UpdateInvoiceStatusModal
        invoice={invoice}
        open={editStatusOpen}
        onClose={() => setEditStatusOpen(false)}
        onSuccess={() => {
          onReload();
        }}
      />
    </div>
  );
};

export default InvoiceInfoCard;

